import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type IconShape =
  | { d: string; solid?: boolean }
  | { circle: readonly [cx: number, cy: number, r: number]; solid?: boolean };

type AppLink = {
  id: string;
  name: string;
  href: string;
  description: string;
  icon: IconShape[];
};

type LayoutState = {
  visibleIds: string[];
};

type ThemeName = "dark" | "light";

type CommandItem = {
  id: string;
  label: string;
  detail: string;
  keywords?: string;
  state?: string;
  run: () => void;
};

const STORAGE_KEY = "excale-directory-layout-v2";
const THEME_STORAGE_KEY = "excale-directory-theme-v1";
const DEFAULT_VISIBLE_IDS = ["regarde", "tunnel"];

const p = (d: string): IconShape => ({ d });
const f = (d: string): IconShape => ({ d, solid: true });
const c = (cx: number, cy: number, r: number, solid = false): IconShape => ({ circle: [cx, cy, r], solid });

const apps: AppLink[] = [
  {
    id: "regarde",
    name: "regarde",
    href: "https://regarde.excale.fr",
    description: "WebRTC screen-sharing",
    icon: [
      p("M8 13h32v20H8z"),
      p("M17 39h14"),
      p("M24 33v6"),
      p("M14 23s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6z"),
      c(24, 23, 3),
    ],
  },
  {
    id: "tunnel",
    name: "tunnel",
    href: "https://tunnel.excale.fr",
    description: "WebRTC file transfer",
    icon: [
      p("M7 17h12v14H7z"),
      p("M29 17h12v14H29z"),
      p("M19 24h10"),
      p("M25 20l4 4-4 4"),
      p("M23 28l-4-4 4-4"),
      p("M11 21h4"),
      p("M33 27h4"),
    ],
  },
  {
    id: "qr",
    name: "qr",
    href: "https://qr.excale.fr",
    description: "QR code generator",
    icon: [
      p("M8 8h12v12H8z"),
      p("M28 8h12v12H28z"),
      p("M8 28h12v12H8z"),
      f("M12 12h4v4h-4z"),
      f("M32 12h4v4h-4z"),
      f("M12 32h4v4h-4z"),
      f("M28 28h4v4h-4z"),
      f("M36 28h4v8h-4z"),
      f("M28 36h8v4h-8z"),
    ],
  },
  {
    id: "guano",
    name: "guano",
    href: "https://guano.excale.fr",
    description: "QRCode based file streaming",
    icon: [
      p("M10 10h11v11H10z"),
      p("M27 10h11v11H27z"),
      p("M10 27h11v11H10z"),
      f("M28 28h4v4h-4z"),
      p("M36 27h2v11h-8"),
      p("M27 36h4"),
      f("M14 14h3v3h-3z"),
      f("M31 14h3v3h-3z"),
      f("M14 31h3v3h-3z"),
    ],
  },
  {
    id: "archi",
    name: "archi",
    href: "https://archi.excale.fr",
    description: "Diagramming tool",
    icon: [
      p("M13 13h10v8H13z"),
      p("M25 27h10v8H25z"),
      p("M10 31h8"),
      p("M18 21v10"),
      p("M23 17h8v10"),
      c(18, 35, 3, true),
      c(35, 17, 3, true),
    ],
  },
  {
    id: "quandbanc",
    name: "quandbanc",
    href: "https://quandbanc.excale.fr",
    description: "Sticky note whiteboard",
    icon: [
      p("M12 11h17v14H12z"),
      p("M20 23h16v15H20z"),
      p("M29 23v6h6"),
      p("M16 16h8"),
      p("M16 20h5"),
      p("M24 31h7"),
    ],
  },
  {
    id: "anonon",
    name: "anonon",
    href: "https://anonon.excale.fr",
    description: "Text anonymization tool",
    icon: [
      p("M10 15h28v18H10z"),
      p("M16 21h16"),
      p("M16 27h9"),
      p("M29 27h5"),
      p("M14 36l20-24"),
      p("M30 34l5 4"),
      p("M35 34l-5 4"),
    ],
  },
  {
    id: "lis",
    name: "lis",
    href: "https://lis.excale.fr",
    description: "On-device screenshot to text",
    icon: [
      p("M16 8H8v8m24-8h8v8M8 32v8h8m24-8v8h-8"),
      p("M16 18h16"),
      p("M16 24h16"),
      p("M16 30h10"),
    ],
  },
  {
    id: "revolution",
    name: "revolution",
    href: "https://revolution.excale.fr",
    description: "Batch 3D model reorient tool",
    icon: [
      p("M24 9l13 7v15l-13 8-13-8V16z"),
      p("M11 16l13 8 13-8"),
      p("M24 24v15"),
      p("M14 13a16 16 0 0 1 22 2"),
      p("M36 15h-7"),
      p("M36 15v-7"),
    ],
  },
];

const pageClass =
  "relative z-10 mx-auto flex h-screen h-[100dvh] w-[min(100%,64rem)] flex-col gap-[clamp(0.7rem,1.7vw,1.2rem)] overflow-hidden p-[clamp(0.65rem,1.5vw,1.25rem)] max-[720px]:gap-[0.6rem] max-[720px]:p-[0.65rem] [@media(max-height:620px)]:gap-[0.45rem]";
const headerClass =
  "grid flex-none min-h-[clamp(5rem,16vh,7.5rem)] grid-cols-[minmax(0,1fr)_auto] items-end gap-3 border-b border-border py-[clamp(0.35rem,1vw,0.75rem)] max-[720px]:min-h-20 max-[420px]:grid-cols-1 max-[420px]:items-start [@media(max-height:620px)]:min-h-0 [@media(max-height:620px)]:pt-[0.2rem] [@media(max-height:620px)]:pb-[0.45rem]";
const eyebrowClass =
  "m-0 mb-[0.35rem] font-pixel text-[clamp(0.52rem,1vw,0.68rem)] leading-[1.25] text-muted-foreground lowercase [@media(max-height:620px)]:hidden";
const titleClass =
  "m-0 font-display text-[clamp(2.25rem,8vw,5rem)] font-normal leading-[0.88] tracking-[0.01em] max-[720px]:text-[clamp(2rem,13vw,3.3rem)] [@media(max-height:620px)]:text-[clamp(1.8rem,7vw,3.4rem)]";
const taglineClass =
  "m-0 mt-[0.4rem] font-pixel text-[clamp(0.52rem,1vw,0.68rem)] leading-[1.25] text-muted-foreground lowercase [@media(max-height:620px)]:hidden";
const headerActionsClass = "flex min-w-0 max-w-[15rem] flex-wrap items-center justify-end gap-[0.35rem] self-end max-[420px]:max-w-full max-[420px]:justify-self-start";
const iconButtonClass =
  "inline-grid h-[2.2rem] w-[2.2rem] cursor-pointer place-items-center rounded-none border border-border bg-background p-0 text-foreground hover:border-foreground hover:bg-secondary focus-visible:border-foreground focus-visible:bg-secondary focus-visible:outline-none aria-[pressed=true]:border-foreground aria-[pressed=true]:bg-secondary";
const commandButtonClass =
  "inline-flex h-[2.2rem] cursor-pointer items-center justify-center rounded-none border border-border bg-background px-[0.55rem] font-pixel text-[0.55rem] leading-none text-foreground lowercase hover:border-foreground hover:bg-secondary focus-visible:border-foreground focus-visible:bg-secondary focus-visible:outline-none";
const themeButtonClass =
  "inline-flex h-[2.2rem] min-w-[3.4rem] cursor-pointer items-center justify-center rounded-none border border-border bg-background px-[0.55rem] font-pixel text-[0.55rem] leading-none text-foreground lowercase hover:border-foreground hover:bg-secondary focus-visible:border-foreground focus-visible:bg-secondary focus-visible:outline-none";
const controlButtonClass =
  "inline-flex min-h-[1.85rem] cursor-pointer items-center justify-center rounded-none border border-border bg-background px-[0.55rem] py-[0.42rem] font-pixel text-[0.55rem] leading-none text-foreground lowercase hover:border-foreground hover:bg-secondary focus-visible:border-foreground focus-visible:bg-secondary focus-visible:outline-none";
const directoryClass =
  "grid min-h-0 flex-1 content-start grid-cols-5 auto-rows-[minmax(6.9rem,auto)] gap-[0.55rem] overflow-y-auto pr-[0.15rem] max-[720px]:grid-cols-3 max-[720px]:auto-rows-[minmax(5.75rem,auto)] max-[720px]:gap-[0.45rem] max-[420px]:grid-cols-2 [@media(max-height:620px)]:auto-rows-[minmax(4.8rem,auto)]";
const emptyLayoutClass =
  "grid min-h-32 place-items-center gap-[0.6rem] border border-dashed border-border p-4 font-pixel text-[0.62rem] text-muted-foreground lowercase";
const customizerClass =
  "grid max-h-[min(18rem,42dvh)] min-h-0 flex-none grid-rows-[auto_minmax(0,1fr)] gap-[0.65rem] overflow-hidden border border-border bg-background p-[0.7rem] max-[720px]:p-[0.55rem]";
const customizerHeadClass =
  "flex items-start justify-between gap-[0.7rem] max-[420px]:grid max-[420px]:grid-cols-1";
const customizerTitleClass = "m-0 font-pixel text-[0.72rem] font-normal leading-none lowercase";
const customizerCopyClass = "m-0 mt-[0.32rem] text-[0.58rem] leading-[1.25] text-muted-foreground";
const appPickerListClass =
  "grid min-h-0 grid-cols-5 gap-[0.45rem] overflow-y-auto pr-[0.15rem] max-[720px]:grid-cols-3 max-[420px]:grid-cols-2";
const appPickerClass =
  "grid min-w-0 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-none border border-border bg-background p-[0.45rem] text-left text-foreground hover:border-foreground focus-visible:border-foreground focus-visible:outline-none aria-[pressed=true]:border-foreground aria-[pressed=true]:bg-secondary max-[420px]:grid-cols-[auto_minmax(0,1fr)]";
const appPickerIconClass = "grid h-8 w-8 place-items-center border border-border bg-card text-foreground";
const appPickerTextClass = "grid min-w-0 gap-[0.2rem]";
const appPickerNameClass =
  "overflow-hidden text-ellipsis whitespace-nowrap font-pixel text-[0.62rem] leading-none lowercase";
const appPickerDescriptionClass =
  "overflow-hidden text-ellipsis whitespace-nowrap text-[0.58rem] leading-[1.25] text-muted-foreground";
const appPickerStateClass =
  "font-pixel text-[0.5rem] leading-[1.25] text-muted-foreground lowercase max-[420px]:col-start-2";
const tileShellClass = "relative h-full min-h-0 min-w-0";
const appLinkClass = "group block h-full min-h-0 min-w-0 text-inherit no-underline focus-visible:outline-none";
const appCardClass =
  "relative grid h-full min-h-0 w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-[0.55rem] overflow-hidden rounded-none border border-border bg-card p-[0.58rem] text-card-foreground no-underline shadow-none transition-colors duration-150 ease-in-out group-hover:border-foreground group-hover:bg-secondary group-focus-visible:border-foreground group-focus-visible:bg-secondary max-[720px]:gap-[0.32rem] max-[720px]:p-[0.45rem] [@media(max-height:620px)]:gap-[0.25rem] [@media(max-height:620px)]:p-[0.4rem]";
const appTopClass = "flex min-w-0 items-start justify-start gap-[0.45rem] p-0";
const appIconClass =
  "grid h-9 w-9 flex-none place-items-center border border-border bg-background text-foreground group-hover:border-foreground group-focus-visible:border-foreground max-[720px]:h-[1.9rem] max-[720px]:w-[1.9rem] [@media(max-height:620px)]:h-[1.75rem] [@media(max-height:620px)]:w-[1.75rem]";
const appContentClass = "grid min-w-0 content-start gap-[0.28rem] p-0";
const appNameClass =
  "overflow-hidden text-ellipsis whitespace-nowrap font-pixel text-[clamp(0.66rem,0.95vw,0.78rem)] leading-none text-foreground lowercase";
const appDescriptionClass =
  "line-clamp-2 overflow-hidden text-[0.58rem] leading-[1.17] text-muted-foreground max-[720px]:line-clamp-1 max-[720px]:text-[0.55rem] max-[420px]:hidden [@media(max-height:620px)]:hidden";
const tileActionsClass =
  "col-span-full grid min-w-0 grid-cols-[1fr_auto_auto_auto] items-center gap-[0.28rem] border-t border-border pt-[0.35rem]";
const dragCueClass =
  "overflow-hidden text-ellipsis whitespace-nowrap font-pixel text-[0.5rem] leading-none text-muted-foreground lowercase";
const tileActionClass =
  "inline-grid h-[1.35rem] w-[1.35rem] cursor-pointer place-items-center rounded-none border border-border bg-background p-0 font-body text-[0.72rem] leading-none text-foreground hover:border-foreground focus-visible:border-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:opacity-50 disabled:hover:border-border";
const paletteBackdropClass =
  "fixed inset-0 z-50 grid place-items-start justify-items-center bg-background/85 p-[clamp(0.65rem,1.5vw,1.25rem)] pt-[clamp(0.75rem,10dvh,6rem)]";
const palettePanelClass =
  "grid max-h-[calc(100dvh-2rem)] min-h-0 w-[min(100%,38rem)] grid-rows-[auto_minmax(0,1fr)] border border-border bg-background";
const paletteInputClass =
  "h-[2.6rem] w-full rounded-none border-0 border-b border-border bg-background px-[0.7rem] font-body text-[0.82rem] text-foreground outline-none placeholder:text-muted-foreground focus:border-foreground";
const paletteListClass = "grid min-h-0 gap-[0.35rem] overflow-y-auto p-[0.45rem]";
const paletteItemClass =
  "grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-[0.55rem] rounded-none border border-border bg-background p-[0.55rem] text-left text-foreground hover:border-foreground hover:bg-secondary focus-visible:border-foreground focus-visible:bg-secondary focus-visible:outline-none";
const paletteItemTextClass = "grid min-w-0 gap-[0.22rem]";
const paletteItemLabelClass = "overflow-hidden text-ellipsis whitespace-nowrap font-pixel text-[0.62rem] leading-none lowercase";
const paletteItemDetailClass = "overflow-hidden text-ellipsis whitespace-nowrap text-[0.58rem] leading-[1.25] text-muted-foreground";
const paletteStateClass =
  "rounded-none border border-border bg-background px-[0.28rem] py-[0.18rem] font-pixel text-[0.5rem] leading-none text-muted-foreground lowercase";
const accessBadgeClass =
  "pointer-events-none absolute -right-px -top-px z-20 min-w-[0.9rem] border border-foreground bg-background px-[0.18rem] py-[0.12rem] text-center font-pixel text-[0.46rem] leading-none text-foreground";

const createDefaultLayout = (): LayoutState => ({ visibleIds: [...DEFAULT_VISIBLE_IDS] });

export function App() {
  const [layout, setLayout] = useState<LayoutState>(() => readSavedLayout());
  const [theme, setTheme] = useState<ThemeName>(() => readSavedTheme());
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isShowingAccessKeys, setIsShowingAccessKeys] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  }, [layout]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.classList.remove("dark", "light");
    document.documentElement.classList.add(theme);
    document.documentElement.style.colorScheme = theme;

    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  const appById = useMemo(() => new Map(apps.map((app) => [app.id, app])), []);
  const visibleApps = useMemo(
    () => layout.visibleIds.map((id) => appById.get(id)).filter((app): app is AppLink => Boolean(app)),
    [appById, layout.visibleIds],
  );

  const commitVisibleIds = (visibleIds: string[]) => {
    setLayout({ visibleIds: normalizeVisibleIds(visibleIds) });
  };

  const moveApp = (id: string, direction: -1 | 1) => {
    const ids = visibleApps.map((app) => app.id);
    const index = ids.indexOf(id);
    const nextIndex = index + direction;

    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return;

    ids.splice(index, 1);
    ids.splice(nextIndex, 0, id);
    commitVisibleIds(ids);
  };

  const dropAppOn = (targetId: string) => {
    if (!draggedId || draggedId === targetId) return;

    const ids = visibleApps.map((app) => app.id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);

    if (from < 0 || to < 0) return;

    const [movedId] = ids.splice(from, 1);
    ids.splice(to, 0, movedId);
    commitVisibleIds(ids);
  };

  const removeApp = (id: string) => {
    commitVisibleIds(layout.visibleIds.filter((visibleId) => visibleId !== id));
  };

  const toggleApp = (id: string) => {
    if (layout.visibleIds.includes(id)) {
      removeApp(id);
      return;
    }

    commitVisibleIds([...layout.visibleIds, id]);
  };

  const resetLayout = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
    }

    setLayout(createDefaultLayout());
  };

  const toggleTheme = () => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  };

  const commandItems = useMemo<CommandItem[]>(() => {
    const visible = new Set(layout.visibleIds);

    return [
      ...apps.map((app) => ({
        id: `open:${app.id}`,
        label: `open ${app.name}`,
        detail: app.description,
        keywords: `${app.name} ${app.description} navigation app`,
        state: visible.has(app.id) ? "shown" : "hidden",
        run: () => {
          window.location.href = app.href;
        },
      })),
      {
        id: "toggle-app-list",
        label: isCustomizing ? "close app list" : "open app list",
        detail: "layout action",
        keywords: "customize apps tiles directory",
        state: "action",
        run: () => setIsCustomizing((current) => !current),
      },
      {
        id: "toggle-theme",
        label: `switch to ${theme === "dark" ? "light" : "dark"} theme`,
        detail: "theme action",
        keywords: "dark light appearance",
        state: theme,
        run: toggleTheme,
      },
      {
        id: "reset-layout",
        label: "restore default layout",
        detail: "layout action",
        keywords: "reset defaults visible apps",
        state: "action",
        run: resetLayout,
      },
      ...apps.map((app) => {
        const isVisible = visible.has(app.id);

        return {
          id: `visibility:${app.id}`,
          label: `${isVisible ? "hide" : "show"} ${app.name}`,
          detail: "visibility action",
          keywords: `${app.name} ${app.description} visible hidden tile`,
          state: isVisible ? "shown" : "hidden",
          run: () => toggleApp(app.id),
        };
      }),
      ...visibleApps.flatMap((app, index) => {
        const items: CommandItem[] = [];

        if (index > 0) {
          items.push({
            id: `move-earlier:${app.id}`,
            label: `move ${app.name} earlier`,
            detail: "layout action",
            keywords: `${app.name} order position up left earlier`,
            state: `${index + 1}/${visibleApps.length}`,
            run: () => moveApp(app.id, -1),
          });
        }

        if (index < visibleApps.length - 1) {
          items.push({
            id: `move-later:${app.id}`,
            label: `move ${app.name} later`,
            detail: "layout action",
            keywords: `${app.name} order position down right later`,
            state: `${index + 1}/${visibleApps.length}`,
            run: () => moveApp(app.id, 1),
          });
        }

        return items;
      }),
    ];
  }, [isCustomizing, layout.visibleIds, theme, visibleApps]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();

      if ((event.ctrlKey || event.metaKey) && key === "k") {
        event.preventDefault();
        setIsCommandPaletteOpen(true);
        return;
      }

      if (event.key === "Alt") {
        setIsShowingAccessKeys(true);
        return;
      }

      if (!event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;

      if (key === "k") {
        event.preventDefault();
        setIsCommandPaletteOpen(true);
        return;
      }

      if (key === "t") {
        event.preventDefault();
        toggleTheme();
        return;
      }

      if (key === "a") {
        event.preventDefault();
        setIsCustomizing((current) => !current);
        return;
      }

      if (key === "r") {
        event.preventDefault();
        resetLayout();
        return;
      }

      const tileNumber = Number.parseInt(key, 10);
      if (tileNumber >= 1 && tileNumber <= 9) {
        if (isCustomizing && tileNumber <= apps.length) {
          event.preventDefault();
          toggleApp(apps[tileNumber - 1].id);
          return;
        }

        if (!isCustomizing && tileNumber <= visibleApps.length) {
          event.preventDefault();
          window.location.href = visibleApps[tileNumber - 1].href;
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Alt") setIsShowingAccessKeys(false);
    };

    const handleBlur = () => setIsShowingAccessKeys(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [isCustomizing, visibleApps]);

  return (
    <>
      <SiteBackground />
      <main className={pageClass} aria-labelledby="page-title">
        <header className={headerClass}>
          <div>
            <p className={eyebrowClass}>excale directory</p>
            <h1 className={titleClass} id="page-title">
              *.excale.fr
            </h1>
            <p className={taglineClass}>small tools, rooms, and utilities</p>
          </div>

          <div className={headerActionsClass}>
            <span className="relative">
              <button
                className={commandButtonClass}
                type="button"
                aria-label="Open command palette"
                onClick={() => setIsCommandPaletteOpen(true)}
              >
                ctrl+k
              </button>
              <AccessBadge isVisible={isShowingAccessKeys}>k</AccessBadge>
            </span>

            <span className="relative">
              <button
                className={themeButtonClass}
                type="button"
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
                onClick={toggleTheme}
              >
                {theme}
              </button>
              <AccessBadge isVisible={isShowingAccessKeys}>t</AccessBadge>
            </span>

            <span className="relative">
              <button
                className={iconButtonClass}
                type="button"
                aria-label={isCustomizing ? "Close app list" : "Open app list"}
                aria-pressed={isCustomizing}
                onClick={() => setIsCustomizing((current) => !current)}
              >
                <WrenchIcon className="h-[1.05rem] w-[1.05rem]" />
              </button>
              <AccessBadge isVisible={isShowingAccessKeys}>a</AccessBadge>
            </span>
          </div>
        </header>

        {isCustomizing ? (
          <AppListCustomizer
            apps={apps}
            visibleIds={layout.visibleIds}
            isShowingAccessKeys={isShowingAccessKeys}
            onReset={resetLayout}
            onToggle={toggleApp}
          />
        ) : null}

        <section className={directoryClass} aria-label="Available apps">
          {visibleApps.length > 0 ? (
            visibleApps.map((app, index) => (
              <AppTile
                key={app.id}
                app={app}
                index={index + 1}
                total={visibleApps.length}
                isCustomizing={isCustomizing}
                isDragging={draggedId === app.id}
                isShowingAccessKeys={isShowingAccessKeys}
                onDragEnd={() => setDraggedId(null)}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", app.id);
                  setDraggedId(app.id);
                }}
                onDrop={() => dropAppOn(app.id)}
                onMove={moveApp}
                onRemove={() => removeApp(app.id)}
              />
            ))
          ) : (
            <div className={emptyLayoutClass}>
              <span>no tiles visible</span>
              <span className="relative">
                <button className={controlButtonClass} type="button" onClick={resetLayout}>
                  restore defaults
                </button>
                <AccessBadge isVisible={isShowingAccessKeys}>r</AccessBadge>
              </span>
            </div>
          )}
        </section>
      </main>
      <CommandPalette isOpen={isCommandPaletteOpen} items={commandItems} onClose={() => setIsCommandPaletteOpen(false)} />
    </>
  );
}

function AppListCustomizer({
  apps,
  visibleIds,
  isShowingAccessKeys,
  onReset,
  onToggle,
}: {
  apps: AppLink[];
  visibleIds: string[];
  isShowingAccessKeys: boolean;
  onReset: () => void;
  onToggle: (id: string) => void;
}) {
  const visible = new Set(visibleIds);

  return (
    <section className={customizerClass} aria-label="App list">
      <div className={customizerHeadClass}>
        <div>
          <h2 className={customizerTitleClass}>app list</h2>
          <p className={customizerCopyClass}>Pick which existing apps appear. Drag visible tiles to change their order.</p>
        </div>

        <span className="relative">
          <button className={controlButtonClass} type="button" onClick={onReset}>
            restore defaults
          </button>
          <AccessBadge isVisible={isShowingAccessKeys}>r</AccessBadge>
        </span>
      </div>

      <div className={appPickerListClass}>
        {apps.map((app, index) => {
          const isVisible = visible.has(app.id);

          return (
            <span className="relative grid min-w-0" key={app.id}>
              <button
                className={appPickerClass}
                type="button"
                aria-pressed={isVisible}
                onClick={() => onToggle(app.id)}
              >
                <span className={appPickerIconClass} aria-hidden="true">
                  <PixelIcon className="h-[1.3rem] w-[1.3rem]" shapes={app.icon} />
                </span>
                <span className={appPickerTextClass}>
                  <span className={appPickerNameClass}>{app.name}</span>
                  <span className={appPickerDescriptionClass}>{app.description}</span>
                </span>
                <span className={appPickerStateClass}>{isVisible ? "shown" : "hidden"}</span>
              </button>
              {index < 9 ? <AccessBadge isVisible={isShowingAccessKeys}>{index + 1}</AccessBadge> : null}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function SiteBackground() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-0 opacity-[0.18] [background:linear-gradient(var(--color-border)_1px,transparent_1px),linear-gradient(90deg,var(--color-border)_1px,transparent_1px),var(--color-background)] [background-size:4rem_4rem]"
      aria-hidden="true"
    />
  );
}

function CommandPalette({
  isOpen,
  items,
  onClose,
}: {
  isOpen: boolean;
  items: CommandItem[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    setQuery("");
    setActiveIndex(0);

    const timeout = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return items;

    return items.filter((item) =>
      `${item.label} ${item.detail} ${item.keywords ?? ""} ${item.state ?? ""}`
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [items, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [filteredItems.length, query]);

  if (!isOpen) return null;

  const executeItem = (item: CommandItem) => {
    item.run();
    onClose();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(0, filteredItems.length - 1)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" && filteredItems[activeIndex]) {
      event.preventDefault();
      executeItem(filteredItems[activeIndex]);
    }
  };

  return (
    <div
      className={paletteBackdropClass}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onKeyDown={handleKeyDown}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={palettePanelClass}>
        <input
          ref={inputRef}
          className={paletteInputClass}
          type="search"
          value={query}
          placeholder="type a command or app name"
          aria-label="Command or app name"
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className={paletteListClass} role="listbox" aria-label="Commands">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <button
                key={item.id}
                className={cn(paletteItemClass, activeIndex === index && "border-foreground bg-secondary")}
                type="button"
                role="option"
                aria-selected={activeIndex === index}
                onClick={() => executeItem(item)}
                onFocus={() => setActiveIndex(index)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className={paletteItemTextClass}>
                  <span className={paletteItemLabelClass}>{item.label}</span>
                  <span className={paletteItemDetailClass}>{item.detail}</span>
                </span>
                {item.state ? <Badge className={paletteStateClass}>{item.state}</Badge> : null}
              </button>
            ))
          ) : (
            <div className="border border-dashed border-border p-[0.7rem] font-pixel text-[0.62rem] text-muted-foreground lowercase">
              no command
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessBadge({ isVisible, children }: { isVisible: boolean; children: ReactNode }) {
  if (!isVisible) return null;

  return <Badge className={accessBadgeClass}>{children}</Badge>;
}

function AppTile({
  app,
  index,
  total,
  isCustomizing,
  isDragging,
  isShowingAccessKeys,
  onDragEnd,
  onDragStart,
  onDrop,
  onMove,
  onRemove,
}: {
  app: AppLink;
  index: number;
  total: number;
  isCustomizing: boolean;
  isDragging: boolean;
  isShowingAccessKeys: boolean;
  onDragEnd: () => void;
  onDragStart: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: () => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const card = (
    <Card className={cn(appCardClass, isCustomizing && "grid-rows-[minmax(0,1fr)_auto] border-muted-foreground")}>
      <div className={appTopClass}>
        <span className={appIconClass} aria-hidden="true">
          <PixelIcon
            className="block h-[1.55rem] w-[1.55rem] overflow-visible max-[720px]:h-[1.35rem] max-[720px]:w-[1.35rem] [@media(max-height:620px)]:h-[1.22rem] [@media(max-height:620px)]:w-[1.22rem]"
            shapes={app.icon}
          />
        </span>
      </div>
      <div className={appContentClass}>
        <span className={appNameClass}>{app.name}</span>
        <span className={appDescriptionClass}>{app.description}</span>
      </div>
      {isCustomizing ? (
        <div className={tileActionsClass} aria-label={`Layout controls for ${app.name}`}>
          <span className={dragCueClass} aria-hidden="true">
            drag
          </span>
          <button
            className={tileActionClass}
            type="button"
            onClick={() => onMove(app.id, -1)}
            disabled={index === 1}
            aria-label={`Move ${app.name} earlier`}
          >
            ↑
          </button>
          <button
            className={tileActionClass}
            type="button"
            onClick={() => onMove(app.id, 1)}
            disabled={index === total}
            aria-label={`Move ${app.name} later`}
          >
            ↓
          </button>
          <button
            className={cn(
              tileActionClass,
              "hover:bg-foreground hover:text-background focus-visible:bg-foreground focus-visible:text-background",
            )}
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${app.name}`}
          >
            ×
          </button>
        </div>
      ) : null}
    </Card>
  );

  return (
    <div
      className={cn(tileShellClass, isCustomizing && "cursor-grab", isDragging && "opacity-[0.45]")}
      draggable={isCustomizing}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        if (!isCustomizing) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }}
      onDragStart={isCustomizing ? onDragStart : undefined}
      onDrop={(event) => {
        if (!isCustomizing) return;

        event.preventDefault();
        onDrop();
      }}
    >
      {!isCustomizing && index <= 9 ? <AccessBadge isVisible={isShowingAccessKeys}>{index}</AccessBadge> : null}
      {isCustomizing ? (
        card
      ) : (
        <a className={appLinkClass} href={app.href}>
          {card}
        </a>
      )}
    </div>
  );
}

function PixelIcon({ className, shapes }: { className?: string; shapes: IconShape[] }) {
  return (
    <svg
      className={cn("fill-none stroke-current", className)}
      viewBox="0 0 48 48"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="square"
      strokeLinejoin="miter"
      focusable="false"
    >
      {shapes.map((shape, index) =>
        "circle" in shape ? (
          <circle
            key={index}
            cx={shape.circle[0]}
            cy={shape.circle[1]}
            r={shape.circle[2]}
            fill={shape.solid ? "currentColor" : undefined}
            stroke={shape.solid ? "none" : undefined}
          />
        ) : (
          <path
            key={index}
            d={shape.d}
            fill={shape.solid ? "currentColor" : undefined}
            stroke={shape.solid ? "none" : undefined}
          />
        ),
      )}
    </svg>
  );
}

function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14.5 5.5 18 2l4 4-3.5 3.5" />
      <path d="m13 7 4 4" />
      <path d="M2 20.5 12.5 10" />
      <path d="m4 22-2-2" />
      <path d="m9 15 3 3" />
    </svg>
  );
}

function readSavedTheme(): ThemeName {
  if (typeof window === "undefined") return "dark";

  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return storedTheme === "light" || storedTheme === "dark" ? storedTheme : "dark";
  } catch {
    return "dark";
  }
}

function readSavedLayout(): LayoutState {
  if (typeof window === "undefined") return createDefaultLayout();

  try {
    const storedLayout = window.localStorage.getItem(STORAGE_KEY);
    if (!storedLayout) return createDefaultLayout();

    const parsedLayout: unknown = JSON.parse(storedLayout);
    if (!isRecord(parsedLayout)) return createDefaultLayout();

    return {
      visibleIds: normalizeVisibleIds(readStringArray(parsedLayout.visibleIds)),
    };
  } catch {
    return createDefaultLayout();
  }
}

function normalizeVisibleIds(value: string[]) {
  const appIds = new Set(apps.map((app) => app.id));

  return [...new Set(value)].filter((id) => appIds.has(id));
}

function readStringArray(value: unknown) {
  if (!Array.isArray(value)) return [];

  return value.filter((item): item is string => typeof item === "string");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
